import { useState, useEffect, useMemo } from 'react'
import moment from 'moment'
import redBallLogo from '../../media/red-ball.png'

const FALLBACK_LOGO = 'https://cricclubs.com/documentsRepo/club_logo/default_club_logo.png'

function safeLogo(path) {
  if (!path) return FALLBACK_LOGO
  // API sometimes returns absolute or relative; handle both
  return path.startsWith('http') ? path : `https://cricclubs.com${path}`
}

function normalizeMatch(m) {
  // Handle a few common field name variants we’ve seen from CricClubs
  const dateStr = m.date || m.match_date || m.matchDate || m.match_schedule_date
  const timeStr = m.time || m.match_time || m.matchTime || m.match_schedule_time
  const location = m.location || m.ground_name || m.ground || m.venue

  const t1Name = m.t1_team_name || m.team1Name || m.home_team_name || m.teamOneName
  const t2Name = m.t2_team_name || m.team2Name || m.away_team_name || m.teamTwoName

  const t1Logo = m.t1_logo_file_path || m.team1Logo || m.home_team_logo
  const t2Logo = m.t2_logo_file_path || m.team2Logo || m.away_team_logo

  // Build a Date from date+time (fall back to date only)
  const dt = timeStr
    ? moment(`${dateStr} ${timeStr}`, [
        'YYYY-MM-DD HH:mm',
        'YYYY-MM-DD h:mm A',
        'MM/DD/YYYY HH:mm',
        'MM/DD/YYYY h:mm A',
        'D-M-YYYY HH:mm',
        'D-M-YYYY h:mm A'
      ])
    : moment(dateStr, ['YYYY-MM-DD', 'MM/DD/YYYY', 'D-M-YYYY'])

  return {
    id: m.match_id || m.id || `${dateStr}-${t1Name}-${t2Name}`,
    day: m.day || (dt.isValid() ? dt.format('dddd') : ''),
    date: dateStr,
    time: timeStr || '',
    when: dt.isValid() ? dt : null,
    location: location || 'TBA',
    t1: { name: t1Name || 'TBD', logo: safeLogo(t1Logo) },
    t2: { name: t2Name || 'TBD', logo: safeLogo(t2Logo) }
  }
}

export default function UpcomingMatches() {
  const [matches, setMatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const consumerKey = atob(process.env.REACT_APP_CONSUMER_KEY)
    const apiKey = atob(process.env.REACT_APP_API_KEY)
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        // ⚠️ Consider proxying this request server-side to hide keys & avoid CORS.
        const targetUrl =
          'https://core-prod-origin.cricclubs.com/core/match/getSchedule?clubId=16685&seriesId=72'

        const response = await fetch(targetUrl, {
          headers: {
            'x-consumer-key': consumerKey,
            'x-api-key': apiKey,
            'X-Requested-With': 'XMLHttpRequest'
          }
        })

        if (!response.ok) {
          throw new Error(`Failed to fetch data: ${response.status}`)
        }

        const data = await response.json()

        if (!data?.data?.fixtureList) {
          throw new Error('Invalid data structure: missing fixtureList')
        }

        // fixtureList may be an object or array
        const raw = Array.isArray(data.data.fixtureList)
          ? data.data.fixtureList
          : Object.values(data.data.fixtureList)

        const normalized = raw.map(normalizeMatch)

        // Upcoming only, with valid datetimes; sort ascending
        const now = moment()
        const upcoming = normalized
          .filter(m => m.when && m.when.isSameOrAfter(now, 'minute'))
          .sort((a, b) => a.when.valueOf() - b.when.valueOf())

        setMatches(upcoming)
      } catch (err) {
        console.error('Fetch error:', err)
        setError(err.message || 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const formatDate = dateString =>
    moment(dateString).isValid()
      ? moment(dateString)
          .format('Do MMM YYYY')
          .replace(/(\d+)(st|nd|rd|th)/, '$1')
      : dateString

  const content = useMemo(() => {
    if (loading) {
      return (
        <div className='d-flex justify-content-center py-5'>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      )
    }
    if (error) {
      return (
        <div className='alert alert-danger my-4'>
          <strong>Error:</strong> {error}
        </div>
      )
    }
    if (!matches.length) {
      return (
        <div className='alert alert-info my-4'>
          No upcoming matches found. Please check back later.
        </div>
      )
    }
    return (
      <div className='row py-5 gy-3 gy-md-0 gy-lg-0'>
        {matches.map(match => (
          <div className='col-md-6 col-lg-4' key={match.id}>
            <div className='upcoming-match-box bg-light rounded shadow-sm p-4 h-100'>
              <h3 className='nxt-match-text py-1 bg-danger text-white text-center rounded mb-3'>
                Next Match
              </h3>

              <p className='date-text text-center fw-bold'>{match.day}</p>
              <p className='date-text text-center mb-4'>
                {match.when ? match.when.format('D MMM YYYY') : formatDate(match.date)}
                {match.time
                  ? ` • ${match.time}`
                  : match.when
                  ? ` • ${match.when.format('h:mm A')}`
                  : ''}
              </p>

              <div className='row align-items-center my-4'>
                <div className='col-5 text-center'>
                  <img
                    src={match.t1.logo}
                    alt={match.t1.name}
                    className='img-fluid rounded-circle border border-2 border-primary p-1'
                    onError={e => (e.currentTarget.src = FALLBACK_LOGO)}
                  />
                  <div className='mt-2 small'>{match.t1.name}</div>
                </div>
                <div className='col-2 text-center'>
                  <p className='mb-0'>
                    <strong className='fs-4 text-danger'>VS</strong>
                  </p>
                </div>
                <div className='col-5 text-center'>
                  <img
                    src={match.t2.logo}
                    alt={match.t2.name}
                    className='img-fluid rounded-circle border border-2 border-primary p-1'
                    onError={e => (e.currentTarget.src = FALLBACK_LOGO)}
                  />
                  <div className='mt-2 small'>{match.t2.name}</div>
                </div>
              </div>

              <p className='location-text text-center mt-4'>
                <i className='bi bi-geo-alt-fill me-2 text-danger'></i>
                Live At {match.location}
              </p>
            </div>
          </div>
        ))}
      </div>
    )
  }, [loading, error, matches])

  return (
    <div className='upcoming-matches'>
      <div className='container'>
        <div className='row'>
          <div className='col-md-12'>
            <div className='top-heading m-auto'>
              <img src={redBallLogo} alt='Red Ball Logo' />
              <span> WHAT'S TRENDING</span>
            </div>
            <h3>
              Upcoming Next Matches In <br />
              2025-T40-Playoffs-2025-T40-Playoff-Div-1
            </h3>
            <p className='up-p py-2'>
              Stay tuned for more updates on our upcoming matches and be sure to join us at the
              ground to cheer on Maverickcc!
            </p>
            {content}
          </div>
        </div>
      </div>
    </div>
  )
}
